import type { ZohoPricingConfigDTO, ZohoSizingRuleDTO } from "../data/onboardingApi";

/**
 * Zoho Books Package Picker v0.2, evaluated in the browser against thresholds
 * loaded from the onboarding_zoho_* tables.
 *
 * Four rules, each yielding a minimum plan; the answer is the highest one.
 * The rules run here rather than server-side so the recommendation updates as
 * the CA toggles answers, but every number comes from the database, so a Zoho
 * price change or a shifted threshold needs no deploy.
 *
 * Per the doc, seat count deliberately does NOT drive the plan: extra seats
 * cost a flat monthly fee on any paid plan. The `seats` rule exists in the
 * table with is_active = 0 and is honoured here if it is ever switched on.
 */

export type ZohoRuleCode = "docs_per_year" | "gstins" | "multi_currency" | "api_calls" | "seats";

const RULE_WHY: Record<string, string> = {
  docs_per_year: "document volume",
  gstins: "GST registrations",
  multi_currency: "needing multi-currency",
  api_calls: "how much processing this creates each month",
  seats: "seat count",
};

export interface SizingAnswers {
  docsPerYear?: string;
  multiCurrency?: boolean;
  gstinCount: number;
  totalSeats: number;
}

export interface ZohoRecommendation {
  plan: string;
  drivingRules: string[];
  needsZohoCall: boolean;
  /** Set when a bucket says no plan can satisfy it (e.g. separate Zoho orgs). */
  blockedBy?: string;
  callsPerDay: number;
}

function rankOf(config: ZohoPricingConfigDTO, planId: string | null): number {
  if (!planId) return 0;
  const plan = config.plans.find((candidate) => candidate.id === planId);
  return plan ? plan.plan_rank : 0;
}

/** First bucket whose inclusive upper bound covers `value` (null = unbounded). */
function bucketFor(buckets: ZohoSizingRuleDTO[] | undefined, value: number): ZohoSizingRuleDTO | undefined {
  if (!buckets || buckets.length === 0) return undefined;
  return (
    buckets.find((bucket) => bucket.threshold_max === null || value <= bucket.threshold_max) ??
    buckets[buckets.length - 1]
  );
}

export function docsPerYearBuckets(config: ZohoPricingConfigDTO): ZohoSizingRuleDTO[] {
  return config.rules.docs_per_year ?? [];
}

export function docsPerYearBucket(
  config: ZohoPricingConfigDTO,
  code: string
): ZohoSizingRuleDTO | undefined {
  return docsPerYearBuckets(config).find((bucket) => bucket.bucket_code === code);
}

export function planById(config: ZohoPricingConfigDTO, id: string) {
  return config.plans.find((plan) => plan.id === id);
}

export function planLabel(config: ZohoPricingConfigDTO, id: string): string {
  return planById(config, id)?.label ?? id;
}

/** The recommended plan and everything above it — never a smaller one. */
export function plansAtOrAbove(config: ZohoPricingConfigDTO, id: string) {
  const rank = rankOf(config, id);
  return config.plans.filter((plan) => plan.plan_rank >= rank);
}

export function recommendZohoPlan(
  config: ZohoPricingConfigDTO,
  answers: SizingAnswers
): ZohoRecommendation | undefined {
  const docsBucket = answers.docsPerYear ? docsPerYearBucket(config, answers.docsPerYear) : undefined;
  if (!docsBucket || answers.multiCurrency === undefined) return undefined;

  // Rule 4: a month's documents land in roughly `working_days_divisor` days,
  // at `calls_per_bill` API calls each.
  const billsPerMonth = (docsBucket.midpoint ?? 0) / 12;
  const callsPerDay = (billsPerMonth / config.settings.working_days_divisor) * config.settings.calls_per_bill;

  const candidates: { rule: string; bucket?: ZohoSizingRuleDTO }[] = [
    { rule: "docs_per_year", bucket: docsBucket },
    { rule: "gstins", bucket: bucketFor(config.rules.gstins, answers.gstinCount) },
    { rule: "multi_currency", bucket: bucketFor(config.rules.multi_currency, answers.multiCurrency ? 1 : 0) },
    { rule: "api_calls", bucket: bucketFor(config.rules.api_calls, callsPerDay) },
    // Present only when the seats rule has been switched on in the DB.
    { rule: "seats", bucket: bucketFor(config.rules.seats, answers.totalSeats) },
  ];

  const blocked = candidates.find((candidate) => candidate.bucket?.flag === "separate_orgs");

  let topRank = 0;
  let driving: string[] = [];
  candidates.forEach((candidate) => {
    if (!candidate.bucket) return;
    const rank = rankOf(config, candidate.bucket.min_plan_id);
    if (rank > topRank) {
      topRank = rank;
      driving = [candidate.rule];
    } else if (rank === topRank && rank > 0) {
      driving.push(candidate.rule);
    }
  });

  const plan = config.plans.find((candidate) => candidate.plan_rank === topRank) ?? config.plans[0];
  if (!plan) return undefined;

  return {
    plan: plan.id,
    drivingRules: driving,
    needsZohoCall: candidates.some((candidate) => candidate.bucket?.flag === "call_zoho"),
    blockedBy: blocked ? blocked.rule : undefined,
    callsPerDay: Math.round(callsPerDay),
  };
}

export function recommendationWhy(drivingRules: string[]): string {
  const labels = drivingRules.map((rule) => RULE_WHY[rule] ?? rule);
  if (labels.length === 0) return "";
  if (labels.length === 1) return `Driven by ${labels[0]}.`;
  if (labels.length === 2) return `Driven by ${labels[0]} and ${labels[1]}.`;
  return `Driven by ${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}.`;
}

/**
 * Seats beyond what the plan includes, at the flat per-seat price — the doc's
 * "just add them" path, shown instead of pushing to a bigger plan.
 */
export function extraSeatCost(
  config: ZohoPricingConfigDTO,
  planId: string,
  totalSeats: number
): { extraSeats: number; monthly: number } {
  const plan = planById(config, planId);
  const included = plan ? plan.users_included : 0;
  const extraSeats = Math.max(0, totalSeats - included);
  return { extraSeats, monthly: extraSeats * config.settings.extra_seat_price };
}
